<template>
  <div ref="textHolder" class="overflow-hidden" v-bind="$attrs">
    {{ text }}
  </div>
  <div class="fixed" ref="sizer" v-bind="$attrs" style="display: none">
    {{ text }}
  </div>
</template>

<script setup lang="ts">
import { debounce } from "perfect-debounce";

interface Props {
  text: string;
}

const props = defineProps<Props>();

const textHolder = ref<HTMLDivElement>();
const sizer = ref<HTMLDivElement>();
const fontSize = ref<string>("");
const lineHeight = computed(
  () => parseFloat(fontSize.value.replace("px", "")) * 1.3 + "px",
);

watch(
  () => props.text,
  (text) => {
    if ((textHolder.value as HTMLDivElement)?.scrollHeight > 0) {
      adjustText();
    }
  },
);

function adjustText() {
  const parentEl = textHolder.value as HTMLDivElement;
  const parentRect = parentEl.getBoundingClientRect();
  parentEl.classList.remove("adjusted-text");
  const parentStyles = window.getComputedStyle(parentEl);
  fontSize.value = parentStyles.fontSize;
  const resizer = sizer.value as HTMLDivElement;
  resizer.style.width = parentRect.width + "px";
  resizer.style.maxWidth = parentRect.width + "px";
  resizer.style.fontSize = fontSize.value;
  resizer.style.fontWeight = parentStyles.fontWeight;
  resizer.style.lineHeight = parentStyles.lineHeight;
  resizer.style.fontFamily = parentStyles.fontFamily;
  resizer.innerText = props.text;
  resizer.style.display = "block";

  let modified = false;
  while (
    resizer.clientHeight > parentRect.height &&
    parseFloat(fontSize.value.replace("px", "")) > 10
  ) {
    modified = true;
    const num = parseFloat(fontSize.value.replace("px", ""));
    fontSize.value = num - 1 + "px";
    resizer.style.fontSize = fontSize.value;
    resizer.style.lineHeight = lineHeight.value;
  }

  resizer.style.display = "none";

  if (!modified) return;
  parentEl.classList.add("adjusted-text");
}

const debouncedAdjust = debounce(adjustText, 100);

onMounted(() => {
  document.fonts.ready.then(() => adjustText());
  window.addEventListener("resize", debouncedAdjust);
  adjustText();
});
onBeforeUnmount(() => {
  window.removeEventListener("resize", debouncedAdjust);
});
</script>

<style scoped lang="postcss">
.adjusted-text {
  font-size: v-bind(fontSize) !important;
  line-height: v-bind(lineHeight) !important;
}
</style>
